'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ReferenceCard } from './reference-card';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ReferenceVideo } from './reference-video';

export const ReferencesList = ({
  url,
  image,
  locale,
}: {
  url?: string;
  image?: string;
  locale: string;
}) => {
  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, locale],
    queryFn: ({ pageParam = 1 }) =>
      fetchReferences({
        locale,
        query: {
          page: pageParam,
          results: 8,
          orderBy: 'created_at',
          order: 'desc',
        },
      }),
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const references = referencesQuery.data?.pages.flatMap(page => page.data) ?? [];

  const firstFourReferences = references.slice(0, 4);
  const remainingReferences = references.slice(4);

  return (
    <>
      <div className={cn('custom-container', 'grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8')}>
        {firstFourReferences.map(reference => (
          <ReferenceCard
            key={reference.id}
            reference={reference}
            className={{ card: 'h-[256px] sm:h-[275px] md:h-[300px] xl:h-[468px]' }}
          />
        ))}
      </div>
      {!!url && <ReferenceVideo image={image || '/images/placeholder.png'} url={url} />}

      <div className={cn('custom-container', 'grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8')}>
        {remainingReferences.map(reference => (
          <ReferenceCard
            key={reference.id}
            reference={reference}
            className={{ card: 'h-[256px] sm:h-[275px] md:h-[300px] xl:h-[468px]' }}
          />
        ))}
        <div
          className={cn('col-span-1 mt-12 flex w-full items-center justify-center sm:col-span-2', {
            hidden: !referencesQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => referencesQuery.fetchNextPage()}
            isLoading={referencesQuery.isFetchingNextPage}
            variant={'outline'}
          >
            Zobraziť viac
          </Button>
        </div>
      </div>
    </>
  );
};
